import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Spinner,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  CardHeader,
  Badge,
} from "reactstrap";
import {
  useGetPipeJackingPermitHealthDeclarationQuery,
  useGetPipejackingPermitHealthDeclarationByIdQuery,
  useUpdatePipeJackingPermitHealthDeclarationIdMutation,
  useCreatePipeJackingPermitHealthDeclarationMutation,
} from "slices/pageSettings/pageSettingsSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

interface PipeJackingPermitHealthDeclarationData {
  id?: number;
  declaration: string;
  text_size: string;
  is_visible: boolean;
}

const validationSchema = Yup.object({
  declaration: Yup.string().optional(),
  text_size: Yup.string().required("Text size is required"),
  is_visible: Yup.boolean().required("Visibility is required"),
});

const PipeJackingPermitHealthDeclarationTextTab: React.FC = () => {
  const [selectedHealthDeclarationId, setSelectedHealthDeclarationId] =
    useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [healthDeclaration, setHealthDeclaration] =
    useState<PipeJackingPermitHealthDeclarationData | null>(null);

  const {
    data: healthDeclarationData,
    refetch,
    isLoading,
  } = useGetPipeJackingPermitHealthDeclarationQuery(undefined);

  const { data: healthDeclarationDetails, refetch: refetchDetail } =
    useGetPipejackingPermitHealthDeclarationByIdQuery(
      selectedHealthDeclarationId,
      {
        skip: !selectedHealthDeclarationId,
      }
    );

  const [updateHealthDeclaration, { isLoading: updateLoading }] =
    useUpdatePipeJackingPermitHealthDeclarationIdMutation();

  const [createHealthDeclaration] =
    useCreatePipeJackingPermitHealthDeclarationMutation();

  useEffect(() => {
    if (healthDeclarationDetails) {
      setHealthDeclaration(healthDeclarationDetails);
    }
  }, [healthDeclarationDetails]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleEditHealthDeclaration = (id: number) => {
    setSelectedHealthDeclarationId(id);
    toggleModal();
  };

  const handleAddHealthDeclaration = () => {
    setSelectedHealthDeclarationId(null);
    setHealthDeclaration({
      declaration: "",
      text_size: "",
      is_visible: false,
    });
    toggleModal();
  };

  const handleSaveHealthDeclaration = async (
    values: PipeJackingPermitHealthDeclarationData
  ) => {
    try {
      if (values.id) {
        await updateHealthDeclaration({
          id: values.id,
          updateData: values,
        }).unwrap();
      } else {
        await createHealthDeclaration(values).unwrap();
      }
      refetch();
      refetchDetail();
      toggleModal();
    } catch (error) {
      console.error("Failed to save health declaration:", error);
    }
  };

  return (
    <div className="container mt-4">
      {isLoading ? (
        <Spinner />
      ) : (
        <Row>
          {healthDeclarationData?.length > 0 ? (
            healthDeclarationData.map(
              (declaration: PipeJackingPermitHealthDeclarationData) => (
                <Col md={10} sm={8} key={declaration.id} className="mb-4">
                  <Card>
                    <CardHeader className="d-flex align-items-center">
                      <h4 className="card-title flex-grow-1 mb-0">
                        Pipe Jacking Permit Health Declaration
                      </h4>
                      <div className="flex-shrink-0">
                        <Button
                          onClick={() =>
                            handleEditHealthDeclaration(declaration.id!)
                          }
                          className="btn btn-soft-info btn-md shadow-none"
                        >
                          Edit
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <div className="table-card">
                        <table className="table mb-0">
                          <tbody>
                            <tr>
                              <td
                                className="fw-medium"
                                style={{ width: "150px" }}
                              >
                                Declaration
                              </td>
                              <td style={{ width: "700px" }}>
                                {declaration.declaration}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium">Text Size</td>
                              <td>{declaration.text_size}</td>
                            </tr>
                            <tr>
                              <td className="fw-medium">Is Visible</td>
                              <td>
                                {declaration.is_visible ? (
                                  <Badge className="bg-success">Yes</Badge>
                                ) : (
                                  <Badge className="bg-danger">No</Badge>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              )
            )
          ) : (
            <Col>
              <p className="text-center">No Health Declarations Found</p>
            </Col>
          )}
        </Row>
      )}
      {(!healthDeclarationData || healthDeclarationData.length === 0) && (
        <Button
          color="primary"
          onClick={handleAddHealthDeclaration}
          className="mb-4"
        >
          <i className="ri-add-line me-1 align-bottom"></i>
          Add Health Declaration
        </Button>
      )}

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {selectedHealthDeclarationId
            ? "Edit Health Declaration"
            : "Add Health Declaration"}
        </ModalHeader>
        <ModalBody>
          {healthDeclaration && (
            <Formik
              initialValues={healthDeclaration}
              validationSchema={validationSchema}
              onSubmit={handleSaveHealthDeclaration}
            >
              {({ values, handleChange, handleBlur }) => (
                <Form>
                  <FormGroup>
                    <Label for="declaration">Declaration</Label>
                    <Field
                      as="textarea"
                      rows={4}
                      id="declaration"
                      name="declaration"
                      value={values.declaration}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-control mt-2"
                    />
                    <ErrorMessage
                      name="declaration"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="text_size">Text Size</Label>
                    <Field
                      as={Input}
                      id="text_size"
                      name="text_size"
                      value={values.text_size}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage
                      name="text_size"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Field
                        type="checkbox"
                        id="is_visible"
                        name="is_visible"
                        checked={values.is_visible}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />{" "}
                      Is Visible
                    </Label>
                    <ErrorMessage
                      name="is_visible"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                  <ModalFooter>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={updateLoading}
                    >
                      {updateLoading ? <Spinner size="sm" /> : "Save"}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      onClick={toggleModal}
                      disabled={updateLoading}
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default PipeJackingPermitHealthDeclarationTextTab;
